/*
  PersonalInfo lives in types/personal.ts. A second, narrower copy used to be
  declared here and shadowed it — which is why adding `phone` and `languages`
  to the data below failed to typecheck even though the real interface already
  allowed them. Re-exported so existing importers of this module still work.
*/
import type { PersonalInfo } from '../types/personal'

export type { PersonalInfo }

export interface AboutContent {
  philosophy: {
    title: string
    content: string
  }
  futurePlans: {
    title: string
    content: string
  }
  funFacts: string[]
  skills: {
    category: string
    items: string[]
  }[]
}

export const personalInfo: PersonalInfo = {
  name: {
    first: 'Abraham',
    last: 'Michael',
    full: 'Abraham Michael'
  },
  taglines: {
    primary: 'MOBILE & FULL-STACK ENGINEER',
    secondary: 'Specializing in Flutter, React Native & Native Android',
    description: 'Software engineer focused on high-performance mobile applications and scalable product ecosystems across Flutter, React Native, Native Android, and modern backend systems.'
  },
  contact: {
    email: 'abrahammgag@gmail.com',
    location: 'Addis Ababa, Ethiopia',
    availability: 'Open to new opportunities',
    phone: '+251945521287',
    website: 'https://abrahamdev.xyz'
  },
  social: {
    github: 'https://github.com/Abmichael',
    linkedin: 'https://www.linkedin.com/in/abrahammgag/'
  },
  about: {
    education: 'BSc In Information Technology (Mekelle University)',
    company: 'Co-founder & Lead Software Engineer at Neo Technologies',
    interests: ['Music (Retro Ethiopian)', 'Movies and Series'],
    languages: [
      { name: 'Tigrigna', level: 'Native' },
      { name: 'English', level: 'EF SET Certified' },
      { name: 'Amharic', level: 'Fluent' }
    ],
    values: ['Product Focus', 'Thoughtful System Design', 'Scalable Architectures', 'Pragmatic Engineering']
  }
}

export const aboutContent: AboutContent = {
  philosophy: {
    title: "My Profile",
    content: "Software engineer and founder of Neo Technologies, focused on building production-grade mobile applications and full-stack systems. Experienced across Flutter, React Native, native Android (Kotlin / Jetpack Compose), backend systems, and cloud infrastructure, with a track record of delivering production software in logistics, transportation, healthcare, and finance. I enjoy solving complex operational problems through offline-first design, background services, scalable architectures, and pragmatic engineering decisions."
  },
  futurePlans: {
    title: "Looking Ahead",
    content: "Graduated with a BSc in Information Technology from Mekelle University in June 2026, and currently expanding Neo Technologies while delivering high-performance mobile and web systems across logistics, transportation, healthcare, and hospitality."
  },
  funFacts: [
    "Enjoys Retro Ethiopian music during focused development sessions",
    "Movie and series enthusiast",
    "EF SET Certified in English"
  ],
  skills: [
    {
      category: "Mobile & Frontend Development",
      items: ["Flutter", "React Native", "Kotlin (Jetpack Compose)", "Dart", "Vue.js", "React", "GetX", "Riverpod", "Tailwind CSS"]
    },
    {
      category: "Backend Development",
      items: ["Node.js", "NestJS", "Express.js", "REST APIs", "Socket.IO", "Auth & RBAC/PBAC", "MVC Architecture"]
    },
    {
      category: "Database & Storage",
      items: ["MongoDB", "Room SQLite", "Realm", "MySQL", "Mongoose", "Prisma ORM", "Data Modeling"]
    },
    {
      category: "DevOps & Mobile Tooling",
      items: ["Git", "GitHub Actions", "Google Play Console", "Play Integrity & FreeRASP", "Nginx", "Docker", "VPS (Ubuntu)", "CI/CD"]
    }
  ]
}

// Animation variants for different sections
export const animationVariants = {
  hero: {
    title: {
      initial: { y: 30, opacity: 0 },
      animate: { y: 0, opacity: 1, transition: { duration: 0.8, ease: 'easeOut' } }
    },
    subtitle: {
      initial: { y: 30, opacity: 0 },
      animate: { y: 0, opacity: 1, transition: { duration: 0.8, delay: 0.3, ease: 'easeOut' } }
    },
    description: {
      initial: { y: 30, opacity: 0 },
      animate: { y: 0, opacity: 1, transition: { duration: 0.8, delay: 0.5, ease: 'easeOut' } }
    },
    buttons: {
      initial: { y: 30, opacity: 0 },
      animate: { y: 0, opacity: 1, transition: { duration: 0.8, delay: 0.7, ease: 'easeOut' } }
    }
  }
}

// Typing animation configuration
export const typingConfig = {
  speed: 80, // milliseconds per character
  cursor: {
    character: '|',
    blinkSpeed: 1000 // milliseconds
  }
}
